import { useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';

/**
 * BeforeAfter — interactive reveal slider.
 *
 * Engineering notes:
 * - Single source of truth: pointer + keyboard both feed one `pct` value.
 * - The range <input> is the a11y surface (keyboard + screen reader) and
 *   is visually hidden via `.ba-range`. The frame handles pointer drag.
 *   `pointer-events: none` on the range is set in CSS so it doesn't
 *   intercept frame drags.
 * - Auto-nudge animation removed: decorative motion with no informational
 *   value is a prototype tell. The handle icon is the affordance.
 */
interface BeforeAfterProps {
  before: ReactNode;
  after: ReactNode;
  /** Aspect ratio for the frame (CSS `aspect-ratio` string). */
  aspect?: string;
  /** Initial slider position, 0..100. */
  startAt?: number;
}

export default function BeforeAfter({
  before,
  after,
  aspect = '16/9',
  startAt = 50,
}: BeforeAfterProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState<number>(startAt);
  const draggingRef = useRef<boolean>(false);

  const commit = (clientX: number): void => {
    const frame = frameRef.current;
    if (!frame) return;
    const r = frame.getBoundingClientRect();
    const v = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(0, Math.min(100, v)));
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>): void => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    commit(e.clientX);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>): void => {
    if (draggingRef.current) commit(e.clientX);
  };
  const onPointerUp = (): void => {
    draggingRef.current = false;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowLeft') setPct((p) => Math.max(0, p - 4));
    if (e.key === 'ArrowRight') setPct((p) => Math.min(100, p + 4));
    if (e.key === 'Home') setPct(0);
    if (e.key === 'End') setPct(100);
  };

  return (
    <div className="ba">
      <div
        className="frame"
        ref={frameRef}
        style={{ aspectRatio: aspect }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="pane before">{before}</div>
        <div className="pane after" style={{ clipPath: `inset(0 0 0 ${pct}%)` }}>
          {after}
        </div>

        <div className="divider" style={{ left: `${pct}%` }} aria-hidden="true" />

        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={Math.round(pct)}
          onChange={(e) => setPct(+e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Reveal redesigned version"
          className="ba-range"
        />

        <div className="handle" style={{ left: `${pct}%` }} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 9l-4 3 4 3M16 9l4 3-4 3" />
          </svg>
        </div>

        <div className="label before-label">Before</div>
        <div className="label after-label">After</div>
      </div>
    </div>
  );
}
